import { useState, useEffect } from "react";
import { addDoc, collection, doc, setDoc } from "firebase/firestore";
import db from "../../firebase/firebase";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Form,
    FormGroup,
    Label,
    Input,
    Button
} from "reactstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { getStorage, ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";

export default function AddEditFractureQuiz() {
    const location = useLocation();
    const navigate = useNavigate();
    const quizCase = location.state?.quizCase || null;

    const [docId, setDocId] = useState(quizCase?.id || "");
    const [title, setTitle] = useState(quizCase?.title || "");
    const [category, setCategory] = useState(quizCase?.category || "");
    const [scenario, setScenario] = useState(quizCase?.scenario || "");
    const [question, setQuestion] = useState(quizCase?.question || "");
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    // Convert choices array to a more manageable object for the form
    const initialChoicesMap = { A: "", B: "", C: "", D: "" };
    if (quizCase?.choices && Array.isArray(quizCase.choices)) {
        quizCase.choices.forEach(c => {
            if (initialChoicesMap[c.key] !== undefined) {
                initialChoicesMap[c.key] = c.text;
            }
        });
    }
    const [choices, setChoices] = useState(initialChoicesMap);

    const [answer, setAnswer] = useState(quizCase?.answer || "");
    const [explanation, setExplanation] = useState(quizCase?.explanation || "");

    // Image states
    const [existingFileUrl, setExistingFileUrl] = useState(quizCase?.image || "");
    const [existingFileName, setExistingFileName] = useState(quizCase?.imageName || "");
    const [existingFilePath, setExistingFilePath] = useState(quizCase?.imagePath || "");
    const [selectedFile, setSelectedFile] = useState(null);

    const storage = getStorage();

    const uploadFileToStorage = (file) => {
        return new Promise((resolve, reject) => {
            try {
                const timestamp = Date.now();
                const filePath = `fractures/${timestamp}_${file.name}`;
                const storageReference = storageRef(storage, filePath);
                const uploadTask = uploadBytesResumable(storageReference, file);

                uploadTask.on(
                    "state_changed",
                    (snapshot) => {
                        const prog = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
                        setUploadProgress(prog);
                    },
                    (error) => {
                        reject(error);
                    },
                    async () => {
                        try {
                            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                            resolve({
                                url: downloadURL,
                                path: filePath,
                                name: file.name,
                            });
                        } catch (err) {
                            reject(err);
                        }
                    }
                );
            } catch (err) {
                reject(err);
            }
        });
    };

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith("image/")) {
            alert("Please upload an image file.");
            e.target.value = null;
            return;
        }

        const maxMB = 10;
        if (file.size > maxMB * 1024 * 1024) {
            alert(`File too large. Maximum allowed size is ${maxMB} MB.`);
            e.target.value = null;
            return;
        }

        setSelectedFile(file);
        setUploadProgress(0);
    };

    const clearSelectedFile = () => {
        setSelectedFile(null);
        setUploadProgress(0);
        document.getElementById("image-upload").value = "";
    };

    const saveCase = async () => {
        if (!title || !answer) {
            alert("Title and Answer are required.");
            return;
        }

        setLoading(true);

        try {
            let fileData = null;

            if (selectedFile) {
                fileData = await uploadFileToStorage(selectedFile);
            }

            if (quizCase && selectedFile && existingFilePath) {
                try {
                    const oldRef = storageRef(storage, existingFilePath);
                    await deleteObject(oldRef).catch((err) => {
                        if (err.code !== "storage/object-not-found") {
                            console.warn("Failed to delete old file:", err);
                        }
                    });
                } catch (err) {
                    console.warn("deleteObject error (ignored):", err);
                }
            }

            const formattedChoices = [
                { key: "A", text: choices["A"] },
                { key: "B", text: choices["B"] },
                { key: "C", text: choices["C"] },
                { key: "D", text: choices["D"] }
            ];

            const payload = {
                title,
                category,
                scenario,
                question,
                choices: formattedChoices,
                answer,
                explanation
            };

            if (fileData) {
                payload.image = fileData.url;
                payload.imageName = fileData.name;
                payload.imagePath = fileData.path;

                setExistingFileUrl(fileData.url);
                setExistingFileName(fileData.name);
                setExistingFilePath(fileData.path);
            } else if (existingFileUrl) {
                payload.image = existingFileUrl;
                payload.imageName = existingFileName;
                payload.imagePath = existingFilePath;
            }

            try {
                if (quizCase?.id) {
                    // Editing existing case
                    await setDoc(doc(db, "fracture-quiz", quizCase.id), payload);
                } else {
                    // Creating new case
                    if (docId) {
                        await setDoc(doc(db, "fracture-quiz", docId), payload);
                    } else {
                        await addDoc(collection(db, "fracture-quiz"), payload);
                    }
                }
                navigate(-1);
            } catch (error) {
                console.error("Error saving case:", error);
                alert("Error saving case.");
            } finally {
                setLoading(false);
                setUploadProgress(0);
            }
        } catch (error) {
            console.error("Error in saveCase:", error);
            alert("Error preparing case data.");
            setLoading(false);
            setUploadProgress(0);
        }
    };

    const updateChoice = (key, value) => {
        setChoices(prev => ({ ...prev, [key]: value }));
    };

    return (
        <Container fluid className="py-4 admin-fracture-editor">
            <Button color="link" onClick={() => navigate(-1)}>
                &larr; Back
            </Button>

            <h3 className="mb-4">
                {quizCase ? "Edit Fracture Case" : "Add Fracture Case"}
            </h3>

            <Card className="mb-4 admin-fracture-editor-card">
                <CardBody>
                    <Form>
                        {!quizCase && (
                            <FormGroup>
                                <Label>Document ID (Optional)</Label>
                                <Input
                                    placeholder="e.g. case-1 or leave blank for auto-generated ID"
                                    value={docId}
                                    onChange={e => setDocId(e.target.value)}
                                />
                            </FormGroup>
                        )}

                        <FormGroup>
                            <Label>Title *</Label>
                            <Input
                                placeholder="e.g. 24-year-old male with ankle pain"
                                value={title}
                                onChange={e => setTitle(e.target.value)}
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label>Category</Label>
                            <Input
                                placeholder="e.g. Upper Extremity"
                                value={category}
                                onChange={e => setCategory(e.target.value)}
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label>Scenario</Label>
                            <Input
                                type="textarea"
                                placeholder="Clinical scenario..."
                                value={scenario}
                                onChange={e => setScenario(e.target.value)}
                                rows={4}
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label>Question</Label>
                            <Input
                                placeholder="e.g. What is the diagnosis?"
                                value={question}
                                onChange={e => setQuestion(e.target.value)}
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label>Image Asset</Label>
                            {existingFileUrl && !selectedFile && (
                                <div style={{ display: "flex", alignItems: "center", gap: 12, padding: 10, border: "1px solid #eee", borderRadius: 6, backgroundColor: "#fafafa" }}>
                                    <div>
                                        <div style={{ fontSize: 14 }}>{existingFileName || "Existing Image"}</div>
                                        <a href={existingFileUrl} target="_blank" rel="noopener noreferrer">
                                            View Image
                                        </a>
                                    </div>
                                    <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                                        <Button
                                            size="sm"
                                            color="primary"
                                            onClick={() => {
                                                alert("Use the Choose File button below to select a new image to replace the existing one.");
                                            }}
                                        >
                                            Replace
                                        </Button>
                                        <Button
                                            size="sm"
                                            color="danger"
                                            onClick={async () => {
                                                if (!existingFilePath) {
                                                    setExistingFileUrl("");
                                                    setExistingFileName("");
                                                    return;
                                                }
                                                const confirmDel = window.confirm("Delete existing image from storage?");
                                                if (!confirmDel) return;

                                                setLoading(true);
                                                try {
                                                    const oldRef = storageRef(storage, existingFilePath);
                                                    await deleteObject(oldRef).catch((err) => {
                                                        if (err.code !== "storage/object-not-found") {
                                                            throw err;
                                                        }
                                                    });

                                                    if (quizCase) {
                                                        const { updateDoc } = await import("firebase/firestore");
                                                        await updateDoc(doc(db, "fracture-quiz", quizCase.id), {
                                                            image: null,
                                                            imageName: null,
                                                            imagePath: null,
                                                        });
                                                    }

                                                    setExistingFileUrl("");
                                                    setExistingFileName("");
                                                    setExistingFilePath("");
                                                    alert("Image removed.");
                                                } catch (err) {
                                                    console.error(err);
                                                    alert("Failed to remove image.");
                                                } finally {
                                                    setLoading(false);
                                                }
                                            }}
                                        >
                                            Delete
                                        </Button>
                                    </div>
                                </div>
                            )}

                            <div style={{ marginTop: 8 }}>
                                <Input
                                    id="image-upload"
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFileChange}
                                />
                                {selectedFile && (
                                    <div style={{ marginTop: 8 }}>
                                        <div>
                                            Selected: <strong>{selectedFile.name}</strong>{" "}
                                            <Button color="link" size="sm" onClick={clearSelectedFile}>
                                                Clear
                                            </Button>
                                        </div>
                                        <div style={{ marginTop: 6, color: "gray" }}>
                                            Upload progress: {uploadProgress}%{" "}
                                            {uploadProgress > 0 && uploadProgress < 100 && <span> (will upload on Save)</span>}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </FormGroup>

                        <h5 className="mt-4 border-bottom pb-2">Choices</h5>
                        {Object.keys(choices).map(key => (
                            <FormGroup key={key}>
                                <Label>Option {key}</Label>
                                <Input
                                    value={choices[key]}
                                    onChange={e => updateChoice(key, e.target.value)}
                                />
                            </FormGroup>
                        ))}

                        <FormGroup className="mt-4">
                            <Label>Correct Answer *</Label>
                            <Input
                                type="select"
                                value={answer}
                                onChange={e => setAnswer(e.target.value)}
                            >
                                <option value="">Select an answer...</option>
                                <option value="A">A</option>
                                <option value="B">B</option>
                                <option value="C">C</option>
                                <option value="D">D</option>
                            </Input>
                        </FormGroup>

                        <FormGroup>
                            <Label>Explanation</Label>
                            <Input
                                type="textarea"
                                placeholder="Explanation for the correct answer..."
                                value={explanation}
                                onChange={e => setExplanation(e.target.value)}
                                rows={4}
                            />
                        </FormGroup>

                        <Row className="mt-4">
                            <Col className="d-flex gap-2">
                                <Button color="primary" onClick={saveCase} disabled={loading}>
                                    {loading ? "Saving..." : "Save Case"}
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </CardBody>
            </Card>
        </Container>
    );
}
